/**
 * Pure helpers for Phase 1 (Location + Group) of the partner-onboarding wizard
 * (FOO-6351 C1). Kept free of React/antd so the validation + payload logic is
 * unit-testable in isolation (see tests/views/restaurant-onboarding).
 */

import AddressHelpers from '../../services/helpers/AddressHelpers';

/** The three mutually-exclusive group choices offered in Phase 1 (plan §5). */
export const GroupOption = {
  Existing: 'existing',
  New: 'new',
  Independent: 'independent',
};

/** Inline slug-availability lifecycle states for the "create new group" flow. */
export const SlugStatus = {
  Idle: 'idle',
  Checking: 'checking',
  Available: 'available',
  Taken: 'taken',
  Invalid: 'invalid',
  Error: 'error',
};

/**
 * Whether Phase 1 collects the state/province: only where the platform splits the
 * country into state-level regions (`useRegions().stateCountries`), because there the
 * state picks the region. Everywhere else the field is hidden.
 */
export function requiresRegion(country, stateCountries = []) {
  return stateCountries.includes(country);
}

/**
 * Normalise free text into a franchise slug: lower-kebab, only the characters
 * the backend accepts (`^[a-zA-Z0-9_-]+$`, see CreateFranchiseRequest).
 */
export function sanitizeSlug(value) {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '');
}

export function isSlugFormatValid(slug) {
  return typeof slug === 'string' && slug.length > 0 && /^[a-zA-Z0-9_-]+$/.test(slug);
}

/** True once every required location field for the chosen country is filled. */
export function isLocationComplete(data, stateCountries = []) {
  const baseFilled = [data.name, data.address, data.city, data.zip_code, data.country].every(Boolean);

  if (!baseFilled) {
    return false;
  }

  return requiresRegion(data.country, stateCountries) ? Boolean(data.state) : true;
}

/** True once the selected brand option has all it needs. */
export function isGroupComplete(data) {
  switch (data.group_option) {
    case GroupOption.Existing:
      return Boolean(data.franchise_id);
    // No fields to fill: the backend provisions a brand named after the
    // restaurant and the Domain step is where it gets named and addressed
    // (Artur's feedback §2).
    case GroupOption.New:
      return true;
    // Independent is no longer offered — every new restaurant joins a group,
    // because the storefront address IS the group's slug. The case stays for
    // records created before the option was withdrawn.
    case GroupOption.Independent:
      return true;
    default:
      return false;
  }
}

/** The overall Phase-1 gate that enables the wizard footer's "Continue" CTA. */
export function canProceed(data) {
  return isLocationComplete(data) && isGroupComplete(data) && isDemoMenuComplete(data);
}

/**
 * A demo restaurant never enters the onboarding wizard, so its menu source is
 * required at creation time. A non-demo restaurant imports its menu later, in
 * the wizard's Menu phase, so nothing is required here.
 */
export function isDemoMenuComplete(data) {
  if (!data?.is_demo) {
    return true;
  }

  return Boolean(data.menu_provider) && Boolean(data.menu_provider_id);
}

/**
 * Payload for POST /dashboard/companies. `franchiseId` is the existing group,
 * the freshly-created group, or null for an independent restaurant (the backend
 * provisions a solo franchise when none is supplied).
 *
 * `progressive: true` selects the partner-onboarding create path (FOO-6351):
 * the company is created from Phase 1 with only location + group, then enriched
 * phase-by-phase. Identity is set in Phase 2 and the owner is provisioned later
 * via the C10 two-actor flow, so neither is sent here — the backend accepts a
 * nullable business_profile + identity for this path and enforces completeness
 * at the go-live preflight instead.
 */
export function buildCompanyPayload(data, franchiseId) {
  return {
    is_demo: Boolean(data.is_demo),
    progressive: true,
    franchise_id: franchiseId ?? null,
    companies: [
      {
        name: data.name,
        address: data.address,
        city: data.city,
        // Always forward whatever Google Places gave us (Berlin, NRW, …). The
        // backend requires `state` on the default path and accepts it nullable
        // on the progressive path; the field is just hidden in the UI outside
        // US/CA/AU. Sending `null` for those countries dropped a value the
        // backend still wanted, which 422'd Phase-1 create.
        state: data.state ?? null,
        country: data.country,
        zip_code: data.zip_code,
        lat: data.lat ?? null,
        lon: data.lon ?? null,
        google_places_id: data.google_place?.place_id ?? null,
        // Demo restaurants skip onboarding entirely, so their menu is collected
        // up-front here instead of in the wizard's Menu phase.
        import_menu: Boolean(data.is_demo && data.menu_provider && data.menu_provider_id),
        menu_provider: data.is_demo ? (data.menu_provider ?? null) : null,
        menu_provider_id: data.is_demo ? (data.menu_provider_id ?? null) : null,
      },
    ],
  };
}

/** `LatLng.lat()` on a live Places result; a plain number once it has been serialised. */
const coordinate = (value) => (typeof value === 'function' ? value() : value) ?? null;

/**
 * Map a Google Places result onto our location form fields (mirrors CompanyLocation).
 *
 * The coordinates are the outlet's own point, not the address string re-parsed
 * by a geocoder, and the backend needs them twice over: every external-delivery
 * quote sends them as the pickup location, and in a state that spans two clocks
 * (Idaho, Texas, British Columbia) they are the only way to tell which zone the
 * outlet keeps. Without them it falls back to the state's default zone and
 * alerts on every create. Always set — a pick without geometry overwrites the
 * previous pick's point with null rather than leaving a stale one behind.
 */
export function placeToLocation(place, GooglePlaceHelpers, regions = []) {
  const country = GooglePlaceHelpers.getCountry(place);

  return {
    name: place.name?.split(',')[0] ?? place.name ?? null,
    google_place: place,
    address: GooglePlaceHelpers.getAddress(place, AddressHelpers.addressFormatFor(regions, country)),
    city: GooglePlaceHelpers.getCity(place),
    state: GooglePlaceHelpers.getState(place),
    country,
    zip_code: GooglePlaceHelpers.getZipCode(place),
    lat: coordinate(place.geometry?.location?.lat),
    lon: coordinate(place.geometry?.location?.lng),
  };
}
